import { Component, signal, HostListener, inject, effect, computed, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PatientMessagesService, PatientMessage, PagedResponse } from '../../core/services/patient-messages.service';
import { SessionService } from '../../core/session.service';
import { ToastService } from '../../core/services/toast.service';
import { ConfirmModalComponent } from '../../component/confirm-modal/confirm-modal.component';
import { EstablishmentsService, EstablishmentDetailsDto } from '../../core/services/establishments.service';
import { flagIcon } from '../../core/utils/flag-icon.util';

@Component({
  selector: 'app-fil-actualite',
  standalone: true,
  imports: [CommonModule, FormsModule, PickerModule, ConfirmModalComponent, TranslatePipe],
  templateUrl: './fil-actualite.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./fil-actualite.component.scss']
})
export class FilActualiteComponent implements OnDestroy {
  private api = inject(PatientMessagesService);
  private session = inject(SessionService);
  private toastService = inject(ToastService);
  private estabService = inject(EstablishmentsService);
  private translate = inject(TranslateService);

  @Input() inline = false;

  nouveauTitre = signal('');
  nouveauMessage = signal('');
  /** Titre/message des langues additionnelles (hors FR, qui a ses propres signals ci-dessus), par code langue */
  nouveauTitreByLang = signal<Record<string, string>>({});
  nouveauMessageByLang = signal<Record<string, string>>({});
  nouvelleCategorie = signal('');
  selectedImage: File | null = null;
  messages = signal<PatientMessage[]>([]);
  loading = signal<boolean>(false);
  error = signal<string | null>(null);
  imageUrls = signal<Map<string, string>>(new Map());
  showEmojiPicker = false;
  /** Code langue dont le sélecteur d'emoji est ouvert (parmi additionalLanguages), ou null */
  openEmojiPickerLang: string | null = null;
  establishment = signal<EstablishmentDetailsDto | null>(null);
  /** Langues patient de l'établissement au-delà du français (fr a toujours ses propres champs dédiés) */
  additionalLanguages = computed(() => (this.establishment()?.langues || []).filter(l => l !== 'fr'));
  hasMultipleLanguages = computed(() => this.additionalLanguages().length > 0);

  isRtl(lang: string): boolean {
    return lang === 'ar';
  }

  readonly flagIcon = flagIcon;

  setNouveauTitreLang(lang: string, value: string): void {
    this.nouveauTitreByLang.update(m => ({ ...m, [lang]: value }));
  }

  setNouveauMessageLang(lang: string, value: string): void {
    this.nouveauMessageByLang.update(m => ({ ...m, [lang]: value }));
  }

  toggleEmojiPickerLang(lang: string): void {
    this.openEmojiPickerLang = this.openEmojiPickerLang === lang ? null : lang;
  }

  addEmojiLang(lang: string, event: any): void {
    if (!event?.emoji?.native) return;
    this.setNouveauMessageLang(lang, (this.nouveauMessageByLang()[lang] || '') + event.emoji.native);
    this.openEmojiPickerLang = null;
  }

  // Sélection multi-sites
  readonly allEstablishments = computed(() => this.session.establishments());
  readonly hasMultipleSites = computed(() => this.allEstablishments().length > 1);
  selectedSiteIds = signal<Set<string>>(new Set());
  showSiteDropdown = false;

  readonly selectedSiteLabel = computed(() => {
    const all = this.allEstablishments();
    const sel = this.selectedSiteIds();
    if (sel.size === 0 || sel.size === all.length) return this.translate.instant('actualite.filActualite.siteSelector.allSites');
    if (sel.size === 1) {
      const id = [...sel][0];
      return all.find(e => e.id === id)?.name ?? this.translate.instant('actualite.filActualite.siteSelector.oneSite');
    }
    return this.translate.instant('actualite.filActualite.siteSelector.multipleSites', { count: sel.size });
  });

  readonly isAllSitesSelected = computed(() => {
    const all = this.allEstablishments();
    return this.selectedSiteIds().size === 0 || this.selectedSiteIds().size === all.length;
  });

  canPublish = computed(() => {
    const hasFr = this.nouveauTitre().trim() && this.nouveauMessage().trim();
    if (!this.hasMultipleLanguages()) return !!hasFr;
    const titles = this.nouveauTitreByLang();
    const messages = this.nouveauMessageByLang();
    const hasAllOthers = this.additionalLanguages().every(lang => (titles[lang] || '').trim() && (messages[lang] || '').trim());
    return !!(hasFr && hasAllOthers);
  });

  editingMessageId = signal<string | null>(null);
  editTitre = '';
  editContenu = '';
  /** Titre/message d'édition des langues additionnelles, par code langue */
  editTitreByLang: Record<string, string> = {};
  editContenuByLang: Record<string, string> = {};
  editCategorie = '';
  /**
   * Gouvernance contenu Prévention (ajustement produit) -- le labo n'a plus aucun contrôle
   * visible sur prevention/topicCode : ces deux champs sont capturés depuis le message existant
   * à l'ouverture de l'édition (startEditMessage) et renvoyés tels quels à la sauvegarde, jamais
   * exposés dans le formulaire. Une actualité Waitora (ex. SPORT) éditée par le labo (titre/texte)
   * conserve donc sa gouvernance Prévention sans que le labo la voie ni la modifie.
   */
  editPrevention = false;
  editTopicCode = '';
  editImage: File | null = null;

  showConfirmModal = signal(false);
  confirmModalTitle = signal('');
  confirmModalMessage = signal('');
  pendingAction: (() => void) | null = null;

  constructor() {
    effect(() => {
      const estId = this.session.selectedId();
      if (estId) {
        this.selectedSiteIds.set(new Set([estId]));
        this.loadEstablishment(estId);
        this.loadMessages(estId);
      }
    });
  }

  toggleSite(id: string) {
    const next = new Set(this.selectedSiteIds());
    if (next.has(id)) { next.delete(id); } else { next.add(id); }
    this.selectedSiteIds.set(next);
  }

  toggleAllSites() {
    if (this.isAllSitesSelected()) {
      const estId = this.session.selectedId();
      this.selectedSiteIds.set(estId ? new Set([estId]) : new Set());
    } else {
      this.selectedSiteIds.set(new Set(this.allEstablishments().map(e => e.id)));
    }
  }

  isSiteSelected(id: string): boolean {
    return this.isAllSitesSelected() || this.selectedSiteIds().has(id);
  }

  private loadEstablishment(establishmentId: string) {
    this.estabService.getDetails(establishmentId).subscribe({
      next: (details) => this.establishment.set(details),
      error: () => this.establishment.set(null)
    });
  }

  private loadMessages(establishmentId: string) {
    this.loading.set(true);
    this.error.set(null);

    this.api.getMessages(establishmentId).subscribe({
      next: (response) => {
        this.revokeImageUrls();
        this.messages.set(response.content);
        this.loading.set(false);
        this.fetchImages(establishmentId, response.content);
      },
      error: (err) => {
        this.error.set(this.translate.instant('actualite.filActualite.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  private fetchImages(establishmentId: string, messages: PatientMessage[]) {
    for (const message of messages) {
      if (!message.imageContentType) continue;
      this.api.getImage(establishmentId, message.id).subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const next = new Map(this.imageUrls());
          next.set(message.id, url);
          this.imageUrls.set(next);
        },
        error: (err) => console.warn('[fil-actualite] échec du chargement de l\'image du message', message.id, err)
      });
    }
  }

  private revokeImageUrls() {
    for (const url of this.imageUrls().values()) {
      URL.revokeObjectURL(url);
    }
    this.imageUrls.set(new Map());
  }

  getImageUrl(messageId: string): string | undefined {
    return this.imageUrls().get(messageId);
  }

  ngOnDestroy() {
    this.revokeImageUrls();
  }

  publierMessage() {
    const estId = this.session.selectedId();
    if (!estId || !this.nouveauTitre().trim() || !this.nouveauMessage().trim()) return;

    if (this.hasMultipleLanguages()) {
      const titles = this.nouveauTitreByLang();
      const messages = this.nouveauMessageByLang();
      const missing = this.additionalLanguages().some(lang => !(titles[lang] || '').trim() || !(messages[lang] || '').trim());
      if (missing) {
        this.toastService.show(this.translate.instant('actualite.filActualite.toast.errorTitle'), 'error', 4000, this.translate.instant('actualite.filActualite.toast.fillBothLanguages'));
        return;
      }
    }

    const targetIds = this.isAllSitesSelected()
      ? this.allEstablishments().map(e => e.id)
      : [...this.selectedSiteIds()];

    if (targetIds.length === 0) targetIds.push(estId);

    const processMessage = async () => {
      const payload: any = {
        titre: this.nouveauTitre(),
        description: this.nouveauMessage(),
        titreI18n: { fr: this.nouveauTitre() },
        descriptionI18n: { fr: this.nouveauMessage() },
        categorie: this.nouvelleCategorie() || undefined,
        // Gouvernance contenu Prévention (ajustement produit) : le composeur labo ne peut plus
        // créer de contenu Prévention -- toujours prevention=false/topicCode=null ici.
        prevention: false,
        topicCode: undefined
      };

      if (this.hasMultipleLanguages()) {
        const titles = this.nouveauTitreByLang();
        const messages = this.nouveauMessageByLang();
        for (const lang of this.additionalLanguages()) {
          payload.titreI18n[lang] = titles[lang];
          payload.descriptionI18n[lang] = messages[lang];
        }
      }

      if (this.selectedImage) {
        try {
          this.validateImage(this.selectedImage);
          payload.imageData = await this.fileToBase64(this.selectedImage);
        } catch (err: any) {
          this.toastService.show(this.translate.instant('actualite.filActualite.toast.errorTitle'), 'error', 4000, err.message);
          return;
        }
      }

      this.api.createMessageMulti(targetIds, payload).subscribe({
        next: (res) => {
          this.nouveauTitre.set('');
          this.nouveauMessage.set('');
          this.nouveauTitreByLang.set({});
          this.nouveauMessageByLang.set({});
          this.nouvelleCategorie.set('');
          this.selectedImage = null;
          this.loadMessages(estId);
          const count = res.count ?? targetIds.length;
          const label = count > 1
            ? this.translate.instant('actualite.filActualite.toast.sitesCount', { count })
            : this.translate.instant('actualite.filActualite.toast.oneSite');
          this.toastService.show(this.translate.instant('actualite.filActualite.toast.successNoted'), 'success', 4000, this.translate.instant('actualite.filActualite.toast.publishedOnSites', { sites: label }));
        },
        error: (err) => {
          const message = err?.error?.message || this.translate.instant('actualite.filActualite.toast.publishError');
          this.toastService.show(this.translate.instant('actualite.filActualite.toast.errorTitle'), 'error', 4000, message);
        }
      });
    };

    processMessage();
  }

  supprimerMessage(messageId: string) {
    this.confirmModalTitle.set(this.translate.instant('actualite.filActualite.confirm.deleteMessageTitle'));
    this.confirmModalMessage.set(this.translate.instant('actualite.filActualite.confirm.deleteMessageMessage'));
    this.pendingAction = () => this.confirmDeleteMessage(messageId);
    this.showConfirmModal.set(true);
  }

  private confirmDeleteMessage(messageId: string) {
    const estId = this.session.selectedId();
    if (!estId) return;

    this.api.deleteMessage(estId, messageId).subscribe({
      next: () => {
        this.loadMessages(estId);
        this.toastService.show(this.translate.instant('actualite.filActualite.toast.deletedTitle'), 'success', 3000, this.translate.instant('actualite.filActualite.toast.messageDeleted'));
      },
      error: (err) => {
        const message = err?.error?.message || this.translate.instant('actualite.filActualite.toast.deleteError');
        this.toastService.show(this.translate.instant('actualite.filActualite.toast.errorTitle'), 'error', 4000, message);
      }
    });
  }

  onConfirmAction() {
    if (this.pendingAction) {
      this.pendingAction();
      this.pendingAction = null;
    }
    this.showConfirmModal.set(false);
  }

  onCancelAction() {
    this.pendingAction = null;
    this.showConfirmModal.set(false);
  }

  startEditMessage(message: PatientMessage) {
    this.editingMessageId.set(message.id);
    this.editTitre = message.titre;
    this.editContenu = message.description;
    this.editTitreByLang = {};
    this.editContenuByLang = {};
    this.editCategorie = message.categorie || '';
    this.editPrevention = message.prevention || false;
    this.editTopicCode = message.topicCode || '';

    if (this.hasMultipleLanguages() && message.titreI18n && message.descriptionI18n) {
      try {
        const titreI18n = JSON.parse(message.titreI18n);
        const descI18n = JSON.parse(message.descriptionI18n);
        for (const lang of this.additionalLanguages()) {
          this.editTitreByLang[lang] = titreI18n[lang] || '';
          this.editContenuByLang[lang] = descI18n[lang] || '';
        }
      } catch (e) {
        console.warn('Failed to parse i18n data:', e);
      }
    }
  }

  cancelEditMessage() {
    this.editingMessageId.set(null);
    this.editTitre = '';
    this.editContenu = '';
    this.editTitreByLang = {};
    this.editContenuByLang = {};
    this.editCategorie = '';
    this.editPrevention = false;
    this.editTopicCode = '';
    this.editImage = null;
  }

  saveEditMessage(messageId: string) {
    const estId = this.session.selectedId();
    if (!estId || !this.editTitre.trim() || !this.editContenu.trim()) return;

    if (this.hasMultipleLanguages()) {
      const missing = this.additionalLanguages().some(lang => !(this.editTitreByLang[lang] || '').trim() || !(this.editContenuByLang[lang] || '').trim());
      if (missing) {
        this.toastService.show(this.translate.instant('actualite.filActualite.toast.errorTitle'), 'error', 4000, this.translate.instant('actualite.filActualite.toast.fillBothLanguages'));
        return;
      }
    }

    const processUpdate = async () => {
      const payload: any = {
        titre: this.editTitre,
        description: this.editContenu,
        titreI18n: { fr: this.editTitre },
        descriptionI18n: { fr: this.editContenu },
        categorie: this.editCategorie || undefined,
        prevention: this.editPrevention,
        topicCode: this.editPrevention ? this.editTopicCode.trim() : undefined
      };

      if (this.hasMultipleLanguages()) {
        for (const lang of this.additionalLanguages()) {
          payload.titreI18n[lang] = this.editTitreByLang[lang];
          payload.descriptionI18n[lang] = this.editContenuByLang[lang];
        }
      }

      if (this.editImage) {
        try {
          this.validateImage(this.editImage);
          payload.imageData = await this.fileToBase64(this.editImage);
        } catch (err: any) {
          this.toastService.show(this.translate.instant('actualite.filActualite.toast.errorTitle'), 'error', 4000, err.message);
          return;
        }
      }

      this.api.updateMessage(estId, messageId, payload).subscribe({
        next: () => {
          this.editingMessageId.set(null);
          this.loadMessages(estId);
          this.toastService.show(this.translate.instant('actualite.filActualite.toast.editedTitle'), 'success', 3000, this.translate.instant('actualite.filActualite.toast.messageEdited'));
        },
        error: (err) => {
          const message = err?.error?.message || this.translate.instant('actualite.filActualite.toast.editError');
          this.toastService.show(this.translate.instant('actualite.filActualite.toast.errorTitle'), 'error', 4000, message);
        }
      });
    };

    processUpdate();
  }

  addEmoji(event: any) {
    if (!event?.emoji?.native) return;
    this.nouveauMessage.set(this.nouveauMessage() + event.emoji.native);
    this.showEmojiPicker = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target || typeof target.closest !== 'function') return;

    try {
      if (!target.closest('.emoji-picker') && !target.closest('.btn-emoji')) {
        this.showEmojiPicker = false;
        this.openEmojiPickerLang = null;
      }
      if (!target.closest('.site-dropdown-wrap')) {
        this.showSiteDropdown = false;
      }
    } catch (err) {
      console.error('Error in document click handler');
    }
  }

  getTimeAgo(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return this.translate.instant('actualite.filActualite.time.now');
    if (diffMins < 60) return this.translate.instant('actualite.filActualite.time.minutesAgo', { minutes: diffMins });
    if (diffHours < 24) return this.translate.instant('actualite.filActualite.time.hoursAgo', { hours: diffHours });
    return this.translate.instant('actualite.filActualite.time.daysAgo', { days: diffDays });
  }

  getSitesLabel(message: PatientMessage): string {
    const all = this.allEstablishments();
    if (!all || all.length <= 1) return '';

    // Si le backend fournit les IDs
    if (message.establishmentIds && message.establishmentIds.length > 0) {
      if (message.establishmentIds.length === all.length) return this.translate.instant('actualite.filActualite.message.allSites');
      const names = message.establishmentIds
        .map(id => all.find(e => e.id === id)?.name)
        .filter(Boolean);
      return names.length > 0 ? names.join(', ') : '';
    }

    // Sinon, on affiche le site courant
    const currentId = this.session.selectedId();
    const current = all.find(e => e.id === currentId);
    return current?.name ?? '';
  }

  getLocalizedText(defaultText: string, i18nJson?: string, lang: string = 'fr'): string | null {
    if (!i18nJson) return null;
    try {
      const translations = JSON.parse(i18nJson);
      const value = translations[lang];
      return value || null;
    } catch {
      return null;
    }
  }

  onImageSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.selectedImage = input.files[0];
    }
  }

  onEditImageSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.editImage = input.files[0];
    }
  }

  removeImage() {
    this.selectedImage = null;
  }

  removeEditImage() {
    this.editImage = null;
  }

  private validateImage(file: File) {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      throw new Error(this.translate.instant('actualite.filActualite.validation.invalidFormat'));
    }

    if (file.size > maxSize) {
      throw new Error(this.translate.instant('actualite.filActualite.validation.imageTooLarge'));
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}
